import { errorToString, NetworkError } from 'helpers/error-helpers';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Button, ButtonColor } from '../buttons/Button';
import styles from './Loading.module.scss';
import { QueryFactory } from 'services/api';
import { isAxiosError } from 'axios';
import { AppLink } from '../buttons/AppLink';
import { Modal } from '../modal/Modal';
import { FeatureFlags } from '../../../application/constants/env-variables.ts';
import clsx from 'clsx';

export type ErrorBoundaryFallbackProps = {
  error: unknown;
  resetError(): void;
};
export const errorBoundaryFallbackFunction = (
  props: ErrorBoundaryFallbackProps,
) => <ErrorBoundaryFallback {...props} />;
export const ErrorBoundaryFallback = (props: ErrorBoundaryFallbackProps) => {
  const { error, resetError } = props;
  const location = window.location.href;
  const { t } = useTranslation();
  const [initialLocation] = useState(window.location.href);
  useEffect(() => {
    if (initialLocation !== location) {
      resetError();
    }
  }, [initialLocation, location, resetError]);

  const errorString = errorToString(error);
  const versionQuery = QueryFactory.VersionQuery.useVersionQuery({
    throwOnError: false,
  });
  const isServerAvailable = !!versionQuery.data;

  // if error is NetworkError, but server is available, there's a high chance we are trying to request an old chunk of JS or CSS
  // (unfortunately it's impossible to get the error code of Network response and compare it with 404).
  // so we show proper error message and offer to reload the whole page
  const isServerUpdated =
    errorString === NetworkError && !isAxiosError(error) && isServerAvailable;

  return (
    <div className={styles.loadingContainer}>
      <div className={clsx(styles.marginTop36)}>
        <div className={styles.loadingData} data-test-id="loading-error">
          {isServerUpdated ? (
            <Modal
              isOpen={true}
              onClose={() => {
                //This modal should only be closed by reloading the page.
              }}
              title={t('suspense.server_updated_title', {
                defaultValue: 'New version',
              })}
              doNotCloseOnOutsideClick={true}
              containerClassName={styles.serverUpdatedModalContainer}
              contentClassName={styles.serverUpdatedModalContent}
            >
              <div className={styles.serverUpdatedModalText}>
                {t('suspense.server_updated')}
              </div>
              <div className={styles.serverUpdatedModalActions}>
                <Button
                  onClick={() => {
                    document.location.reload();
                  }}
                  title={t('suspense.reload')}
                  color={ButtonColor.Primary}
                />
              </div>
            </Modal>
          ) : (
            <div className={styles.loading}>
              <div className={styles.errorFallback}>
                <h1>{errorString}</h1>
                <AppLink to={'/'}>{t('suspense.go_to_main_screen')}</AppLink>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
